import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  createAssetProcessingDoc,
  exportDomToPdf,
  failAssetDoc,
  finalizeAssetDoc,
  uploadBlobToStorage,
} from './printablesExport';
import {
  formatBytes,
  getImageDimensions,
  loadFlyerDraftPrefs,
  saveFlyerDraftPrefs,
  uploadFlyerImage,
  validateImage,
} from './flyerUploadUtils';
import './SaleRentFlyerPrintable.css';

const PAGE_SIZE_MM = { width: 210, height: 297 };
const DEFAULT_DESCRIPTION = 'A premium home with curated interiors and seamless access to the city.';
const DEFAULT_HIGHLIGHTS = ['3 Beds', '2 Baths', '1850 sq.ft'];
const DEFAULT_SPECS = {
  bhk: '3 BHK',
  area: '1850 sq.ft',
  facing: 'East',
  possession: 'Ready to move',
};
const TITLE_MAX = 60;
const DESCRIPTION_MAX = 180;
const MAX_HIGHLIGHTS = 5;
const AMENITY_PREVIEW_MAX = 6;
const AMENITY_OPTIONS = [
  'Parking',
  'Lift',
  'Power Backup',
  'Security',
  'Gym',
  'Pool',
  'Clubhouse',
  'Garden',
  'Near Metro',
  'Near School',
];

const buildDraftId = (uid) => {
  const suffix = uid ? uid.slice(-4) : 'draft';
  return `flyer-${Date.now()}-${suffix}`;
};

const buildDefaultDraft = ({ branchCity, draftId }) => ({
  draftId,
  listingType: 'sale',
  flyerMode: 'minimal',
  title: 'Premium 3BHK Apartment',
  location: branchCity || '',
  price: '',
  priceMode: 'exact',
  description: DEFAULT_DESCRIPTION,
  highlights: DEFAULT_HIGHLIGHTS,
  amenities: [],
  specs: DEFAULT_SPECS,
  ctaLine: 'Schedule a private viewing today',
  ctaButtonLabel: 'Schedule a Visit',
  heroImageUrl: '',
  image2Url: '',
  image3Url: '',
  qrMode: 'none',
  qrImageUrl: '',
});

const mergeDraft = (base, saved) => {
  if (!saved) return base;
  return {
    ...base,
    ...saved,
    specs: { ...base.specs, ...(saved.specs || {}) },
    highlights: Array.isArray(saved.highlights) ? saved.highlights : base.highlights,
    amenities: Array.isArray(saved.amenities) ? saved.amenities : base.amenities,
  };
};

const SaleRentFlyerPrintable = ({
  firebase,
  authUser,
  tenantId,
  branchId,
  profile,
  branch,
  brandKit,
  assignedEmail,
  assignedEmailMissing,
  compliance,
}) => {
  const pageRef = useRef(null);
  const heroInputRef = useRef(null);
  const image2InputRef = useRef(null);
  const image3InputRef = useRef(null);
  const qrInputRef = useRef(null);
  const saveTimerRef = useRef(null);

  const [draft, setDraft] = useState(() =>
    buildDefaultDraft({ branchCity: branch.city, draftId: buildDraftId(authUser?.uid) }),
  );
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [customAmenity, setCustomAmenity] = useState('');

  const [heroMeta, setHeroMeta] = useState({ fileName: '', sizeLabel: '', width: 0, height: 0 });
  const [image2Meta, setImage2Meta] = useState({ fileName: '', sizeLabel: '', width: 0, height: 0 });
  const [image3Meta, setImage3Meta] = useState({ fileName: '', sizeLabel: '', width: 0, height: 0 });
  const [qrMeta, setQrMeta] = useState({ fileName: '', sizeLabel: '', width: 0, height: 0 });

  const [uploading, setUploading] = useState({ hero: false, image2: false, image3: false, qr: false });
  const [uploadError, setUploadError] = useState({ hero: '', image2: '', image3: '', qr: '' });
  const [uploadWarning, setUploadWarning] = useState({ hero: '', image2: '', image3: '', qr: '' });

  const [saveToAssets, setSaveToAssets] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  useEffect(() => {
    let active = true;
    const loadDraft = async () => {
      if (!tenantId || !authUser?.uid || !firebase) {
        setDraftLoaded(true);
        return;
      }
      try {
        const saved = await loadFlyerDraftPrefs(firebase, tenantId, authUser.uid);
        if (!active) return;
        const draftId = saved?.draftId || buildDraftId(authUser.uid);
        const base = buildDefaultDraft({ branchCity: branch.city, draftId });
        const merged = mergeDraft(base, saved);
        setDraft(merged);
        if (!saved?.draftId) {
          await saveFlyerDraftPrefs(firebase, tenantId, authUser.uid, merged);
        }
      } catch (loadErr) {
        console.error('Flyer draft load failed:', loadErr);
      } finally {
        if (active) setDraftLoaded(true);
      }
    };
    loadDraft();
    return () => {
      active = false;
    };
  }, [authUser?.uid, branch.city, firebase, tenantId]);

  useEffect(() => {
    if (!draftLoaded || !tenantId || !authUser?.uid || !firebase) return undefined;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveFlyerDraftPrefs(firebase, tenantId, authUser.uid, draft).catch((saveErr) => {
        console.error('Flyer draft save failed:', saveErr);
      });
    }, 500);

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [authUser?.uid, draft, draftLoaded, firebase, tenantId]);

  const updateDraft = (patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const updateSpecs = (key, value) => {
    setDraft((prev) => ({
      ...prev,
      specs: { ...prev.specs, [key]: value },
    }));
  };

  const updateHighlights = (index, value) => {
    setDraft((prev) => {
      const next = [...prev.highlights];
      next[index] = value;
      return { ...prev, highlights: next };
    });
  };

  const addHighlight = () => {
    setDraft((prev) => {
      if (prev.highlights.length >= MAX_HIGHLIGHTS) return prev;
      return { ...prev, highlights: [...prev.highlights, ''] };
    });
  };

  const removeHighlight = (index) => {
    setDraft((prev) => ({
      ...prev,
      highlights: prev.highlights.filter((_, idx) => idx !== index),
    }));
  };

  const toggleAmenity = (amenity) => {
    setDraft((prev) => {
      const exists = prev.amenities.includes(amenity);
      const next = exists
        ? prev.amenities.filter((item) => item !== amenity)
        : [...prev.amenities, amenity];
      return { ...prev, amenities: next };
    });
  };

  const addCustomAmenity = () => {
    const value = customAmenity.trim();
    if (!value) return;
    setDraft((prev) => {
      if (prev.amenities.includes(value)) return prev;
      return { ...prev, amenities: [...prev.amenities, value] };
    });
    setCustomAmenity('');
  };

  const removeAmenity = (amenity) => {
    setDraft((prev) => ({
      ...prev,
      amenities: prev.amenities.filter((item) => item !== amenity),
    }));
  };

  const ensureDraftId = () => {
    if (draft.draftId) return draft.draftId;
    const nextId = buildDraftId(authUser?.uid);
    setDraft((prev) => ({ ...prev, draftId: nextId }));
    return nextId;
  };

  const handleImageUpload = async ({ field, file, maxMB, minWidthWarn, minWidthRequired, inputRef }) => {
    if (!file) return;
    setUploadError((prev) => ({ ...prev, [field]: '' }));
    setUploadWarning((prev) => ({ ...prev, [field]: '' }));

    const validation = validateImage(file, maxMB, minWidthWarn);
    if (!validation.isValid) {
      setUploadError((prev) => ({ ...prev, [field]: validation.errors.join(' ') }));
      return;
    }

    try {
      const dimensions = await getImageDimensions(file);
      if (minWidthRequired && (dimensions.width < minWidthRequired || dimensions.height < minWidthRequired)) {
        setUploadError((prev) => ({
          ...prev,
          [field]: `Minimum resolution is ${minWidthRequired}x${minWidthRequired}px.`,
        }));
        return;
      }

      if (minWidthWarn && (dimensions.width < minWidthWarn || dimensions.height < minWidthWarn)) {
        setUploadWarning((prev) => ({
          ...prev,
          [field]: `Recommended minimum width is ${minWidthWarn}px.`,
        }));
      }

      if (!tenantId || !authUser?.uid || !firebase) {
        setUploadError((prev) => ({ ...prev, [field]: 'Upload unavailable. Please try again later.' }));
        return;
      }

      setUploading((prev) => ({ ...prev, [field]: true }));
      const draftId = ensureDraftId();
      const uploadResult = await uploadFlyerImage({
        firebase,
        storagePath: `tenants/${tenantId}/flyerUploads/${authUser.uid}/${draftId}/${field}`,
        file,
      });

      const fieldMap = {
        hero: 'heroImageUrl',
        image2: 'image2Url',
        image3: 'image3Url',
        qr: 'qrImageUrl',
      };
      const targetField = fieldMap[field] || `${field}ImageUrl`;
      updateDraft({ [targetField]: uploadResult.url });

      const meta = {
        fileName: file.name || `${field}.jpg`,
        sizeLabel: formatBytes(file.size),
        width: dimensions.width,
        height: dimensions.height,
      };

      if (field === 'hero') setHeroMeta(meta);
      if (field === 'image2') setImage2Meta(meta);
      if (field === 'image3') setImage3Meta(meta);
      if (field === 'qr') setQrMeta(meta);
    } catch (uploadErr) {
      console.error('Flyer image upload failed:', uploadErr);
      setUploadError((prev) => ({ ...prev, [field]: 'Upload failed. Please try again.' }));
    } finally {
      setUploading((prev) => ({ ...prev, [field]: false }));
      if (inputRef?.current) {
        inputRef.current.value = '';
      }
    }
  };

  const clearImage = (field) => {
    const fieldMap = {
      hero: 'heroImageUrl',
      image2: 'image2Url',
      image3: 'image3Url',
      qr: 'qrImageUrl',
    };
    const targetField = fieldMap[field] || `${field}ImageUrl`;
    updateDraft({ [targetField]: '' });
    setUploadError((prev) => ({ ...prev, [field]: '' }));
    setUploadWarning((prev) => ({ ...prev, [field]: '' }));
    if (field === 'hero') setHeroMeta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    if (field === 'image2') setImage2Meta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    if (field === 'image3') setImage3Meta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    if (field === 'qr') setQrMeta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
  };

  const handleReset = () => {
    const fresh = buildDefaultDraft({ branchCity: branch.city, draftId: draft.draftId || buildDraftId(authUser?.uid) });
    setDraft(fresh);
    setHeroMeta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    setImage2Meta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    setImage3Meta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    setQrMeta({ fileName: '', sizeLabel: '', width: 0, height: 0 });
    setUploadError({ hero: '', image2: '', image3: '', qr: '' });
    setUploadWarning({ hero: '', image2: '', image3: '', qr: '' });
  };

  const titleCount = draft.title ? draft.title.length : 0;
  const descriptionCount = draft.description ? draft.description.length : 0;
  const titleTooLong = titleCount > TITLE_MAX;
  const descriptionTooLong = descriptionCount > DESCRIPTION_MAX;

  const highlightItems = draft.highlights
    .map((item) => String(item || '').trim())
    .filter((item) => item);
  const highlightPreview = highlightItems.slice(0, 3);
  const extraHighlightCount = Math.max(0, highlightItems.length - highlightPreview.length);

  const specItems = [
    { label: 'BHK', value: draft.specs.bhk },
    { label: 'Area', value: draft.specs.area },
    { label: 'Facing', value: draft.specs.facing },
    { label: 'Possession', value: draft.specs.possession },
  ].filter((item) => String(item.value || '').trim());
  const specPreview = specItems.slice(0, 4);

  const amenityItems = draft.amenities
    .map((item) => String(item || '').trim())
    .filter((item) => item);
  const amenityPreview =
    draft.flyerMode === 'detailed' ? amenityItems.slice(0, AMENITY_PREVIEW_MAX) : [];
  const extraAmenityCount = Math.max(0, amenityItems.length - amenityPreview.length);

  const priceLabel = draft.priceMode === 'request' ? 'On Request' : draft.price || 'On Request';
  const descriptionPreview = draft.description?.trim()
    ? draft.description.trim()
    : 'Add a short description for this property.';
  const heroTitle = draft.title || 'Property Title';
  const heroLocation = draft.location || 'Location';
  const contactPhone = profile.phone || 'Phone needed';
  const contactWhatsApp =
    profile.whatsapp && profile.whatsapp !== profile.phone ? profile.whatsapp : '';

  const missing = useMemo(() => {
    const list = [];
    if (compliance.requirePhone && !profile.phone) list.push('phone');
    if (compliance.requireRera && !branch.reraId) list.push('rera');
    if (compliance.requireAddress && !branch.address) list.push('address');
    if (draft.qrMode === 'upload' && !draft.qrImageUrl) list.push('qr');
    return list;
  }, [branch.address, branch.reraId, compliance, draft.qrImageUrl, draft.qrMode, profile.phone]);

  const isBlocked = missing.length > 0;
  const statusLabel = isBlocked ? 'Missing Requirements' : 'Ready';
  const canSave = Boolean(tenantId && branchId && authUser?.uid);

  const accent = brandKit?.colors?.accent || '#c8a951';
  const showAddress = compliance.requireAddress && branch.address;
  const showRera = compliance.requireRera && branch.reraId;

  const handleExport = async () => {
    if (isBlocked || !pageRef.current) return;
    setExporting(true);
    setError('');
    setWarning('');
    setMessage('');

    let assetRef = null;
    try {
      const fileName = `Douglas-McCartney-${draft.listingType === 'sale' ? 'Sale' : 'Rent'}-Flyer-${profile.fullName || 'franchisee'}.pdf`;
      const blob = await exportDomToPdf({
        element: pageRef.current,
        fileName,
        pageSizeMm: PAGE_SIZE_MM,
        backgroundColor: '#ffffff',
        marginMm: 8,
      });

      if (saveToAssets) {
        if (!canSave) {
          setWarning('Upload failed, file not saved.');
        } else {
          try {
            const snapshot = {
              listingType: draft.listingType,
              title: draft.title,
              location: draft.location,
              price: draft.price,
              priceMode: draft.priceMode,
              description: draft.description,
              highlights: highlightItems,
              amenities: draft.amenities,
              specs: draft.specs,
              ctaLine: draft.ctaLine,
              ctaButtonLabel: draft.ctaButtonLabel,
              heroImageUrl: draft.heroImageUrl,
              image2Url: draft.image2Url,
              image3Url: draft.image3Url,
              qrMode: draft.qrMode,
              qrImageUrl: draft.qrImageUrl,
              contact: {
                fullName: profile.fullName || '',
                designation: profile.designation || '',
                phone: profile.phone || '',
                assignedEmail: assignedEmail || '',
              },
              brand: {
                colors: brandKit?.colors || {},
                disclaimerText: brandKit?.disclaimerText || '',
                logoUrl: brandKit?.logoUrl || '',
                requireRera: compliance.requireRera,
                requireAddress: compliance.requireAddress,
              },
            };

            assetRef = await createAssetProcessingDoc({
              firebase,
              tenantId,
              branchId,
              authUserId: authUser?.uid || '',
              type: 'sale_rent_flyer',
              snapshot,
            });

            const storagePath = `tenants/${tenantId}/assets/${assetRef.id}/sale-rent-flyer.pdf`;
            const url = await uploadBlobToStorage({
              firebase,
              storagePath,
              blob,
              contentType: 'application/pdf',
              metadata: { tenantId, branchId, createdBy: authUser?.uid || '' },
            });

            await finalizeAssetDoc({
              firebase,
              assetRef,
              output: { pdfUrl: url, storagePath },
            });
          } catch (uploadError) {
            console.error('Flyer asset upload failed:', uploadError);
            setWarning('Upload failed, file not saved.');
            if (assetRef) {
              await failAssetDoc({ firebase, assetRef, errorMessage: uploadError.message });
            }
          }
        }
      }
      setMessage('Flyer downloaded.');
    } catch (err) {
      console.error('Flyer export failed:', err);
      setError('Could not generate PDF. Try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="printable-layout">
      <div className="printable-preview sale-rent-flyer-preview">
        <div className="printable-page sale-rent-flyer-page" ref={pageRef}>
          <div className="sale-rent-flyer-inner">
            <div className="sale-rent-flyer-hero">
              {draft.heroImageUrl ? (
                <img src={draft.heroImageUrl} alt="Property" crossOrigin="anonymous" />
              ) : (
                <div className="sale-rent-flyer-hero-placeholder">Upload Hero Image</div>
              )}
              {brandKit?.logoUrl && (
                <img
                  className="sale-rent-flyer-hero-watermark"
                  src={brandKit.logoUrl}
                  alt="Brand watermark"
                  crossOrigin="anonymous"
                />
              )}
              <div className="sale-rent-flyer-hero-tag" style={{ backgroundColor: accent }}>
                {draft.listingType === 'sale' ? 'FOR SALE' : 'FOR RENT'}
              </div>
              <div className="sale-rent-flyer-hero-overlay">
                <div className="sale-rent-flyer-hero-text">
                  <h2 className="sale-rent-flyer-hero-title">{heroTitle}</h2>
                  <div className="sale-rent-flyer-hero-location">{heroLocation}</div>
                </div>
                <div className="sale-rent-flyer-hero-price">{priceLabel}</div>
              </div>
            </div>

            <div className="sale-rent-flyer-info">
              <div className="sale-rent-flyer-info-grid">
                <div className="sale-rent-flyer-specs">
                  <div className="sale-rent-flyer-section-title">Key Specs</div>
                  {specPreview.length > 0 ? (
                    specPreview.map((item) => (
                      <div className="sale-rent-flyer-spec" key={item.label}>
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                      </div>
                    ))
                  ) : (
                    <div className="sale-rent-flyer-empty">Add specs</div>
                  )}
                </div>
                <div className="sale-rent-flyer-highlights">
                  <div className="sale-rent-flyer-section-title">Highlights</div>
                  <div className="sale-rent-flyer-highlights-list">
                    {highlightPreview.length > 0 ? (
                      highlightPreview.map((item, index) => (
                        <span key={`${item}-${index}`} className="sale-rent-flyer-chip" style={{ borderColor: accent }}>
                          {item}
                        </span>
                      ))
                    ) : (
                      <span className="sale-rent-flyer-empty">Add highlights</span>
                    )}
                    {extraHighlightCount > 0 && (
                      <span className="sale-rent-flyer-more">+{extraHighlightCount} more</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="sale-rent-flyer-description">{descriptionPreview}</div>

              {draft.flyerMode === 'detailed' && amenityPreview.length > 0 && (
                <div className="sale-rent-flyer-amenities">
                  <div className="sale-rent-flyer-section-title">Amenities</div>
                  <div className="sale-rent-flyer-amenities-row">
                    {amenityPreview.map((item) => (
                      <span key={item} className="sale-rent-flyer-amenity-chip">
                        {item}
                      </span>
                    ))}
                  </div>
                  {extraAmenityCount > 0 && (
                    <div className="sale-rent-flyer-more">+{extraAmenityCount} more</div>
                  )}
                </div>
              )}
            </div>

            <div className="sale-rent-flyer-cta">
              <div className="sale-rent-flyer-cta-line">{draft.ctaLine || 'Schedule a private viewing'}</div>
              <div className="sale-rent-flyer-cta-contact">
                <span>{contactPhone}</span>
                {contactWhatsApp && <span>WhatsApp {contactWhatsApp}</span>}
              </div>
              {draft.qrMode === 'upload' && (
                <div className="sale-rent-flyer-cta-qr">
                  {draft.qrImageUrl ? (
                    <img src={draft.qrImageUrl} alt="QR" crossOrigin="anonymous" />
                  ) : (
                    <div className="sale-rent-flyer-qr-placeholder">QR</div>
                  )}
                </div>
              )}
            </div>

            <div className="sale-rent-flyer-footer">
              <div className="sale-rent-flyer-footer-brand">
                {branch.branchName || 'Douglas & McCartney'}
                {branch.city ? ` - ${branch.city}` : ''}
              </div>
              {showAddress && <div className="sale-rent-flyer-footer-text">{branch.address}</div>}
              {showRera && <div className="sale-rent-flyer-footer-text">RERA: {branch.reraId}</div>}
              {brandKit?.disclaimerText && (
                <div className="sale-rent-flyer-footer-disclaimer">{brandKit.disclaimerText}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="printable-panel">
        <div className={`printable-status ${isBlocked ? 'error' : 'success'}`}>
          {statusLabel}
        </div>
        {assignedEmailMissing && (
          <div className="printable-banner warning">Assigned email not set yet.</div>
        )}
        {missing.includes('qr') && (
          <div className="printable-banner error">Upload QR to include QR.</div>
        )}
        {missing.includes('phone') && (
          <div className="printable-banner error">
            Phone required.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {missing.includes('address') && (
          <div className="printable-banner error">
            Address required.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {missing.includes('rera') && (
          <div className="printable-banner error">
            RERA required.
            <button type="button" className="printable-link" onClick={() => window.location.assign('/account')}>
              Fix in Profile
            </button>
          </div>
        )}
        {message && <div className="printable-banner info">{message}</div>}
        {warning && <div className="printable-banner warning">{warning}</div>}
        {error && <div className="printable-banner error">{error}</div>}

        <div className="flyer-panel-section">
          <h4>Images</h4>
          <div className="flyer-upload-row">
            <div>
              <strong>Hero image</strong>
              <div className="flyer-upload-meta">{heroMeta.sizeLabel || 'No file yet'}</div>
              {uploadWarning.hero && <div className="flyer-upload-warning">{uploadWarning.hero}</div>}
              {uploadError.hero && <div className="flyer-upload-error">{uploadError.hero}</div>}
            </div>
            <div className="flyer-upload-actions">
              <button
                type="button"
                className="printable-action"
                onClick={() => heroInputRef.current?.click()}
                disabled={uploading.hero}
              >
                {draft.heroImageUrl ? 'Replace' : 'Upload'}
              </button>
              {draft.heroImageUrl && (
                <button type="button" className="printable-action" onClick={() => clearImage('hero')}>
                  Remove
                </button>
              )}
            </div>
          </div>
          <input
            ref={heroInputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(event) =>
              handleImageUpload({
                field: 'hero',
                file: event.target.files?.[0],
                maxMB: 5,
                minWidthWarn: 1200,
                inputRef: heroInputRef,
              })
            }
          />

          <div className="flyer-upload-grid">
            <div className="flyer-upload-item">
              <span>Gallery Image 1</span>
              <button
                type="button"
                className="printable-action"
                onClick={() => image2InputRef.current?.click()}
                disabled={uploading.image2}
              >
                {draft.image2Url ? 'Replace' : 'Upload'}
              </button>
              {draft.image2Url && (
                <button type="button" className="printable-action" onClick={() => clearImage('image2')}>
                  Remove
                </button>
              )}
              {uploadError.image2 && <div className="flyer-upload-error">{uploadError.image2}</div>}
            </div>
            <div className="flyer-upload-item">
              <span>Gallery Image 2</span>
              <button
                type="button"
                className="printable-action"
                onClick={() => image3InputRef.current?.click()}
                disabled={uploading.image3}
              >
                {draft.image3Url ? 'Replace' : 'Upload'}
              </button>
              {draft.image3Url && (
                <button type="button" className="printable-action" onClick={() => clearImage('image3')}>
                  Remove
                </button>
              )}
              {uploadError.image3 && <div className="flyer-upload-error">{uploadError.image3}</div>}
            </div>
          </div>

          <input
            ref={image2InputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(event) =>
              handleImageUpload({
                field: 'image2',
                file: event.target.files?.[0],
                maxMB: 5,
                minWidthWarn: 0,
                inputRef: image2InputRef,
              })
            }
          />
          <input
            ref={image3InputRef}
            type="file"
            accept="image/png,image/jpeg"
            style={{ display: 'none' }}
            onChange={(event) =>
              handleImageUpload({
                field: 'image3',
                file: event.target.files?.[0],
                maxMB: 5,
                minWidthWarn: 0,
                inputRef: image3InputRef,
              })
            }
          />
        </div>

        <div className="flyer-panel-section">
          <h4>Listing Details</h4>
          <div className="flyer-fields">
            <label>
              Listing Type
              <select value={draft.listingType} onChange={(event) => updateDraft({ listingType: event.target.value })}>
                <option value="sale">For Sale</option>
                <option value="rent">For Rent</option>
              </select>
            </label>
            <label>
              Flyer Mode
              <select value={draft.flyerMode} onChange={(event) => updateDraft({ flyerMode: event.target.value })}>
                <option value="minimal">Minimal (Recommended)</option>
                <option value="detailed">Detailed (Adds amenities)</option>
              </select>
            </label>
            <label>
              Property Title
              <input
                type="text"
                value={draft.title}
                onChange={(event) => updateDraft({ title: event.target.value })}
              />
              <div className={`flyer-field-meta ${titleTooLong ? 'warning' : ''}`}>
                {titleCount}/{TITLE_MAX}
                {titleTooLong ? ' · Too long, will be trimmed' : ''}
              </div>
            </label>
            <label>
              Location
              <input
                type="text"
                value={draft.location}
                onChange={(event) => updateDraft({ location: event.target.value })}
              />
            </label>
            <label>
              Price / Rent
              <input
                type="text"
                value={draft.price}
                onChange={(event) => updateDraft({ price: event.target.value })}
              />
            </label>
            <label>
              Price label mode
              <select value={draft.priceMode} onChange={(event) => updateDraft({ priceMode: event.target.value })}>
                <option value="exact">Show exact value</option>
                <option value="request">On Request</option>
              </select>
            </label>
          </div>
        </div>

        <div className="flyer-panel-section">
          <h4>Highlights</h4>
          <div className="flyer-highlight-list">
            {[0, 1, 2].map((index) => (
              <div className="flyer-highlight-row" key={`highlight-${index}`}>
                <input
                  type="text"
                  value={draft.highlights[index] || ''}
                  onChange={(event) => updateHighlights(index, event.target.value)}
                />
              </div>
            ))}
          </div>
          <div className="flyer-field-note">Extra highlights can be added in Advanced options.</div>
        </div>

        <details className="flyer-accordion">
          <summary>Advanced Options</summary>
          <div className="flyer-accordion-body">
            <div className="flyer-panel-section">
              <h4>Description</h4>
              <div className="flyer-fields">
                <label>
                  Description (max 400 chars)
                  <textarea
                    rows={3}
                    value={draft.description}
                    onChange={(event) => updateDraft({ description: event.target.value.slice(0, 400) })}
                  />
                </label>
              </div>
              <div className={`flyer-field-meta ${descriptionTooLong ? 'warning' : ''}`}>
                {descriptionCount}/{DESCRIPTION_MAX}
                {descriptionTooLong ? ' · Too long, will be trimmed' : ''}
              </div>
            </div>

            <div className="flyer-panel-section">
              <h4>Extra Highlights</h4>
              <div className="flyer-highlight-list">
                {draft.highlights.slice(3).map((item, index) => (
                  <div className="flyer-highlight-row" key={`highlight-extra-${index}`}>
                    <input
                      type="text"
                      value={item}
                      onChange={(event) => updateHighlights(index + 3, event.target.value)}
                    />
                    <button
                      type="button"
                      className="printable-action"
                      onClick={() => removeHighlight(index + 3)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                {draft.highlights.length < MAX_HIGHLIGHTS && (
                  <button type="button" className="printable-action" onClick={addHighlight}>
                    Add Highlight
                  </button>
                )}
              </div>
            </div>

            <div className="flyer-panel-section">
              <h4>Amenities</h4>
              <div className="flyer-field-note">Shown on flyer only in Detailed mode.</div>
              <div className="flyer-amenities">
                {AMENITY_OPTIONS.map((amenity) => (
                  <label key={amenity} className="flyer-amenity-item">
                    <input
                      type="checkbox"
                      checked={draft.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                    />
                    {amenity}
                  </label>
                ))}
              </div>
              <div className="flyer-custom-amenity">
                <input
                  type="text"
                  value={customAmenity}
                  onChange={(event) => setCustomAmenity(event.target.value)}
                  placeholder="Custom amenity"
                />
                <button type="button" className="printable-action" onClick={addCustomAmenity}>
                  Add
                </button>
              </div>
              {draft.amenities
                .filter((amenity) => !AMENITY_OPTIONS.includes(amenity))
                .map((amenity) => (
                  <div key={amenity} className="flyer-custom-item">
                    <span>{amenity}</span>
                    <button type="button" className="printable-action" onClick={() => removeAmenity(amenity)}>
                      Remove
                    </button>
                  </div>
                ))}
            </div>

            <div className="flyer-panel-section">
              <h4>QR Block</h4>
              <div className="flyer-fields">
                <label>
                  QR Mode
                  <select value={draft.qrMode} onChange={(event) => updateDraft({ qrMode: event.target.value })}>
                    <option value="none">No QR</option>
                    <option value="upload">Upload QR Image</option>
                  </select>
                </label>
              </div>
              {draft.qrMode === 'upload' && (
                <div className="flyer-upload-row">
                  <div>
                    <strong>QR image</strong>
                    <div className="flyer-upload-meta">{qrMeta.sizeLabel || 'No file yet'}</div>
                    {uploadError.qr && <div className="flyer-upload-error">{uploadError.qr}</div>}
                  </div>
                  <div className="flyer-upload-actions">
                    <button
                      type="button"
                      className="printable-action"
                      onClick={() => qrInputRef.current?.click()}
                      disabled={uploading.qr}
                    >
                      {draft.qrImageUrl ? 'Replace' : 'Upload'}
                    </button>
                    {draft.qrImageUrl && (
                      <button type="button" className="printable-action" onClick={() => clearImage('qr')}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="flyer-upload-hint">2MB max / PNG or JPG only / 600x600px minimum</div>
                </div>
              )}
              <input
                ref={qrInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={(event) =>
                  handleImageUpload({
                    field: 'qr',
                    file: event.target.files?.[0],
                    maxMB: 2,
                    minWidthWarn: 600,
                    minWidthRequired: 600,
                    inputRef: qrInputRef,
                  })
                }
              />
            </div>
          </div>
        </details>

        <div className="flyer-panel-section">
          <h4>Specs</h4>
          <div className="flyer-specs-fields">
            <label>
              BHK
              <input type="text" value={draft.specs.bhk} onChange={(event) => updateSpecs('bhk', event.target.value)} />
            </label>
            <label>
              Area (sqft)
              <input type="text" value={draft.specs.area} onChange={(event) => updateSpecs('area', event.target.value)} />
            </label>
            <label>
              Facing
              <input type="text" value={draft.specs.facing} onChange={(event) => updateSpecs('facing', event.target.value)} />
            </label>
            <label>
              Possession
              <input
                type="text"
                value={draft.specs.possession}
                onChange={(event) => updateSpecs('possession', event.target.value)}
              />
            </label>
          </div>
        </div>

        <div className="flyer-panel-section">
          <h4>CTA</h4>
          <div className="flyer-fields">
            <label>
              CTA Line
              <input type="text" value={draft.ctaLine} onChange={(event) => updateDraft({ ctaLine: event.target.value })} />
            </label>
          </div>
        </div>

        <div className="printable-actions">
          <label className="printable-checkbox">
            <input
              type="checkbox"
              checked={saveToAssets}
              onChange={(event) => setSaveToAssets(event.target.checked)}
            />
            Save to Assets Library
          </label>
          <button type="button" className="printable-action" onClick={handleReset}>
            Reset to Default
          </button>
          <button
            type="button"
            className="printable-action"
            disabled={exporting || isBlocked}
            onClick={handleExport}
          >
            {exporting ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SaleRentFlyerPrintable;
